/**
 * Governance → Desktop mascot gallery → Day outfits, without React (unit-tested in apps/admin/test/day-outfits.test.ts).
 * Owner, 2026-10-06: holiday and fun outfits "auto swap" in for everyone on their day, with the day's greeting in place
 * of "What can we work on?"; people can still change outfit, and after the day they are back on their own pick. A day
 * outfit has a day rule instead of a gallery window, and it is never featured. On gyro-svc's own date engine
 * (mascotDays.ts, a copy tested against it), this file holds:
 *  - the form: its inputs and the day rule they make, and back (dayRuleFromInputs, dayInputsOf), the greeting's counter
 *    and refusals (the service's own sanitiser), and the live "Next: 3 dates (Pacific)";
 *  - the tabs (Today, Scheduled, Needs a date, Past, Retired) and the "Needs attention" list, worked out again as the
 *    clock moves, by the Pacific calendar (the engine's own dayLifeState and showingSomewhere, which the service's view
 *    uses too), and which day outfit is swapped in today;
 *  - the warnings in words, the stars ("★ 4.3 (12)" and each star's share), and the notes after a change;
 *  - the bodies Add, Edit and Restore send, each with both revisions the card loaded. Edit renames with PATCH (name
 *    only: a day outfit's catalog window is only the day an older gyro-svc shows it on, and is never sent) and changes
 *    the day with PUT …/day.
 * The service checks everything again; nothing here is trusted.
 */
import {
  DAY_DEFAULT_ZONE,
  DAY_GREETING_MAX,
  DAY_MAX_EASTER_OFFSET,
  DAY_MAX_EXPLICIT_YEARS,
  DAY_MAX_SPAN,
  DAY_MAX_YEAR,
  DAY_MIN_YEAR,
  DayIndex,
  MONTH_NAMES,
  civilAt,
  civilOfIso,
  civilParts,
  dayLifeState,
  isoOfCivil,
  nextOccurrences,
  parseDayRule,
  sanitizeDayGreeting,
  showingSomewhere,
  weekdayOfCivil,
  type DayEntry,
  type DayOccurrence,
  type DayRule,
} from './mascotDays';
import {
  nameRefusal,
  outfitPicture,
  outfitState,
  type AddDayOutfitBody,
  type AdminDay,
  type AdminOutfit,
  type DayOutfitKind,
  type DayState,
  type DayWarning,
  type DesktopMascotAdded,
  type DesktopMascotCatalog,
  type KeptOutfit,
  type OutfitRating,
  type RestoreOutfitBody,
  type UpdateDayBody,
  type UpdateMascotOutfitBody,
} from './desktopMascot';

/** The calendar the console judges day outfits by: Pacific time, the company's, which apps that send no zone get too. */
export const DAY_CONSOLE_ZONE = DAY_DEFAULT_ZONE;
/** adminSchema.ts mascotCollection: a collection's name ("Core collection"), after trimming. */
export const DAY_COLLECTION_MAX = 40;

const DAY_MS = 24 * 60 * 60_000;
/** The last day a month can have in any year, February counted as 29 (as the engine counts it). */
const MONTH_MAX_DAY = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const quoted = (name: string): string => `“${name}”`;
const hasOwn = (record: object, key: string): boolean => Object.prototype.hasOwnProperty.call(record, key);

// ---- The form ----

/** The rule kinds in the order the form's selector offers them, with its words. */
export const DAY_RULE_KINDS: ReadonlyArray<{ kind: DayRule['kind']; label: string }> = [
  { kind: 'fixed', label: 'The same date every year' },
  { kind: 'nth-weekday', label: 'A weekday of a month, every year' },
  { kind: 'easter', label: 'Days from Easter, every year' },
  { kind: 'explicit', label: 'A date IT enters for each year' },
  { kind: 'once', label: 'Once' },
];

/** The nth-weekday choices: the first to fourth, or the last, weekday of the month (a fifth is refused: some months have none). */
export const NTH_CHOICES: ReadonlyArray<{ n: number; label: string }> = [
  { n: 1, label: 'First' },
  { n: 2, label: 'Second' },
  { n: 3, label: 'Third' },
  { n: 4, label: 'Fourth' },
  { n: -1, label: 'Last' },
];

/** A day outfit's kind in the form and on its badge. */
export const DAY_KIND_LABEL: Record<DayOutfitKind, string> = { holiday: 'Holiday', fun: 'Fun day' };

/**
 * The day fields as the form holds them: every rule kind's own fields at once (switching kinds keeps what was typed),
 * the numbers people type as text (so "-4" can be typed on the way to "-47"), and the greeting, kind and collection.
 */
export interface DayInputs {
  rule: DayRule['kind'];
  /** fixed and nth-weekday: 1 (January) to 12. */
  month: number;
  /** fixed: the day of the month (February up to 29: that date comes in leap years only). */
  day: number;
  /** nth-weekday: 0 (Sunday) to 6. */
  weekday: number;
  /** nth-weekday: 1 to 4, or -1 for the last. */
  n: number;
  /** easter: days from Easter Sunday, as typed (Mardi Gras is -47). */
  easterOffset: string;
  /** explicit: one start date a year, 'YYYY-MM-DD' (a date input each), in any order; empty ones are left out. */
  dates: string[];
  /** explicit and once: how many days it lasts, as typed. */
  spanDays: string;
  /** explicit: the date is announced later each year and should be checked. */
  confirmEachYear: boolean;
  /** once: its first day, 'YYYY-MM-DD'. */
  date: string;
  greeting: string;
  kind: DayOutfitKind;
  /** Optional ("Core collection"); empty: none. */
  collection: string;
}

/** The days a month can have, February counted as 29 (the fixed rule's day choices). */
export function monthDayCount(month: number): number {
  return MONTH_MAX_DAY[month - 1] ?? 31;
}

/** A new day outfit's fields: the same date every year, today's date by the Pacific calendar, no greeting yet. */
export function defaultDayInputs(now: number): DayInputs {
  const today = civilAt(DAY_CONSOLE_ZONE, now);
  const { month, day } = civilParts(today);
  return {
    rule: 'fixed',
    month,
    day,
    weekday: weekdayOfCivil(today),
    n: 1,
    easterOffset: '0',
    dates: [''],
    spanDays: '1',
    confirmEachYear: false,
    date: isoOfCivil(today),
    greeting: '',
    kind: 'holiday',
    collection: '',
  };
}

/** The fields for a stored day outfit (its rule's own fields filled in, the others ready as for a new one). */
export function dayInputsOf(day: Pick<AdminDay, 'rule' | 'greeting' | 'kind' | 'collection'>, now: number): DayInputs {
  const inputs: DayInputs = { ...defaultDayInputs(now), rule: day.rule.kind, greeting: day.greeting, kind: day.kind, collection: day.collection ?? '' };
  const rule = day.rule;
  switch (rule.kind) {
    case 'fixed':
      return { ...inputs, month: rule.month, day: rule.day };
    case 'nth-weekday':
      return { ...inputs, month: rule.month, weekday: rule.weekday, n: rule.n };
    case 'easter':
      return { ...inputs, easterOffset: String(rule.offsetDays) };
    case 'explicit':
      return { ...inputs, dates: Object.keys(rule.dates).sort().map((year) => rule.dates[year]!), spanDays: String(rule.spanDays), confirmEachYear: rule.confirmEachYear };
    case 'once':
      return { ...inputs, date: rule.date, spanDays: String(rule.spanDays) };
    default:
      // A kind from a newer gyro-svc (this console is older): never offered for editing (dayRows), but never a crash.
      return { ...inputs, rule: 'fixed' };
  }
}

/** A whole number as typed ("-47", " 8 "), or undefined for anything else ("", "-", "1.5", "1e3"). */
function wholeNumber(text: string): number | undefined {
  if (!/^\s*[-+]?\d{1,4}\s*$/.test(text)) return undefined;
  const value = Number(text.trim());
  return Object.is(value, -0) ? 0 : value;
}

/** A date an explicit or one-off rule may name, as the engine takes it: a real date from DAY_MIN_YEAR to DAY_MAX_YEAR. */
function ruleDateRefusal(value: string): string | null {
  const civil = civilOfIso(value);
  if (civil !== undefined) {
    const { year } = civilParts(civil);
    if (year >= DAY_MIN_YEAR && year <= DAY_MAX_YEAR) return null;
  }
  return `Each date must be a real date from ${DAY_MIN_YEAR} to ${DAY_MAX_YEAR}; “${value}” is not.`;
}

export type DayRuleRead = { ok: true; rule: DayRule } | { ok: false; message: string };

/**
 * The day rule the fields describe, or why the service would refuse it, in the form's words: a date the month does not
 * have, days from Easter beyond 70, an explicit rule with no date, two dates for one year or more than 30 years, a span
 * outside 1 to 14 days, a one-off with no date. Whatever passes is read back by the engine's own parseDayRule.
 */
export function dayRuleFromInputs(inputs: DayInputs): DayRuleRead {
  const refused = (message: string): DayRuleRead => ({ ok: false, message });
  const span = wholeNumber(inputs.spanDays);
  const spanRefusal = span !== undefined && span >= 1 && span <= DAY_MAX_SPAN ? null : `How many days it lasts must be a whole number from 1 to ${DAY_MAX_SPAN}.`;
  let candidate: unknown;
  switch (inputs.rule) {
    case 'fixed':
      if (inputs.day > monthDayCount(inputs.month)) return refused(`${MONTH_NAMES[inputs.month - 1] ?? 'That month'} has at most ${monthDayCount(inputs.month)} days.`);
      candidate = { kind: 'fixed', month: inputs.month, day: inputs.day };
      break;
    case 'nth-weekday':
      candidate = { kind: 'nth-weekday', month: inputs.month, weekday: inputs.weekday, n: inputs.n };
      break;
    case 'easter': {
      const offset = wholeNumber(inputs.easterOffset);
      if (offset === undefined || Math.abs(offset) > DAY_MAX_EASTER_OFFSET) {
        return refused(`Days from Easter must be a whole number from -${DAY_MAX_EASTER_OFFSET} to ${DAY_MAX_EASTER_OFFSET}: below 0 is before Easter Sunday (Mardi Gras is -47), 0 is Easter Sunday itself.`);
      }
      candidate = { kind: 'easter', offsetDays: offset };
      break;
    }
    case 'explicit': {
      const entered = inputs.dates.map((value) => value.trim()).filter((value) => value.length > 0);
      if (entered.length === 0) return refused('Enter the date for at least one year.');
      if (entered.length > DAY_MAX_EXPLICIT_YEARS) return refused(`At most ${DAY_MAX_EXPLICIT_YEARS} years of dates; this rule has ${entered.length}.`);
      const dates: Record<string, string> = {};
      for (const value of entered) {
        const dateRefusal = ruleDateRefusal(value);
        if (dateRefusal) return refused(dateRefusal);
        const year = value.slice(0, 4);
        if (hasOwn(dates, year)) return refused(`Each year has one date, and ${year} is listed twice.`);
        dates[year] = value;
      }
      if (spanRefusal) return refused(spanRefusal);
      candidate = { kind: 'explicit', dates, spanDays: span, confirmEachYear: inputs.confirmEachYear };
      break;
    }
    case 'once': {
      const value = inputs.date.trim();
      if (!value) return refused('Choose its date.');
      const dateRefusal = ruleDateRefusal(value);
      if (dateRefusal) return refused(dateRefusal);
      if (spanRefusal) return refused(spanRefusal);
      candidate = { kind: 'once', date: value, spanDays: span };
      break;
    }
  }
  const rule = parseDayRule(candidate);
  return rule ? { ok: true, rule } : refused('This day rule is not one Gyro can use. Check its fields.');
}

/** The greeting as the service stores it: NFC-normalised, runs of white space made one space, trimmed. */
export function greetingText(raw: string): string {
  return raw.normalize('NFC').replace(/\s+/g, ' ').trim();
}

/** The greeting's length as its limit counts it: code points of greetingText (an emoji is 1 or more: ❤️ and a flag 2, a joined one more). */
export function greetingLength(raw: string): number {
  return Array.from(greetingText(raw)).length;
}

/**
 * Why the service would refuse a greeting, or null (its sanitiser decides): empty, longer than DAY_GREETING_MAX code
 * points, or holding a line break, a control character or an invisible formatting character.
 */
export function greetingRefusal(raw: string): string | null {
  if (sanitizeDayGreeting(raw) !== undefined) return null;
  const length = greetingLength(raw);
  if (length === 0) return 'Write the greeting people see all day in place of “What can we work on?”.';
  if (length > DAY_GREETING_MAX) return `The greeting may be at most ${DAY_GREETING_MAX} characters (some emoji count as 2 or more); this one has ${length}.`;
  return 'The greeting must be plain text on one line: no line breaks, tabs, control characters or invisible formatting characters.';
}

/** Why the service would refuse a collection, or null: at most 40 characters after trimming, no control characters (empty: none). */
export function collectionRefusal(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length > DAY_COLLECTION_MAX) return `The collection may be at most ${DAY_COLLECTION_MAX} characters; this one has ${trimmed.length}.`;
  if (/[\u0000-\u001f\u007f]/.test(trimmed)) return 'The collection must not contain control characters such as tabs or line breaks.';
  return null;
}

/** What a day outfit is besides its name and image: the rule, the greeting as stored, the kind and the collection. */
export interface DayDetails {
  day: DayRule;
  greeting: string;
  kind: DayOutfitKind;
  collection: string | null;
}

/** The day details the fields describe, or the first reason the service would refuse them: the rule, the greeting, the collection. */
export function dayDetails(inputs: DayInputs): { ok: true; details: DayDetails } | { ok: false; message: string } {
  const rule = dayRuleFromInputs(inputs);
  if (!rule.ok) return rule;
  const greeting = sanitizeDayGreeting(inputs.greeting);
  if (greeting === undefined) return { ok: false, message: greetingRefusal(inputs.greeting) ?? 'Check the greeting.' };
  const collection = collectionRefusal(inputs.collection);
  if (collection) return { ok: false, message: collection };
  return { ok: true, details: { day: rule.rule, greeting, kind: inputs.kind, collection: inputs.collection.trim() || null } };
}

/** Two rules that mean the same days (the engine's own reading of each, so key order never matters). */
export function sameDayRule(left: DayRule, right: DayRule): boolean {
  return JSON.stringify(parseDayRule(left) ?? null) === JSON.stringify(parseDayRule(right) ?? null);
}

/** What the card says while the service cannot read where it keeps day outfits: nothing about them can change. */
export const DAY_STORE_UNREADABLE_TEXT = 'Day outfits, retired outfits and their warnings could not be read just now, so they cannot be added or changed. '
  + 'The gallery above is as stored. Reload the page in a minute.';

export type DayOutfitAdd = { kind: 'refused'; message: string } | { kind: 'ready'; body: AddDayOutfitBody };

/**
 * The POST body for "Add to the gallery" as a day outfit, or why it would be refused (the name first, then the day
 * fields). Both revisions the card loaded; null for the annex's means the service could not read it.
 */
export function dayOutfitAdd(input: { pngBase64: string; name: string; day: DayInputs }, expectedRevision: number, expectedAnnexRevision: number | null): DayOutfitAdd {
  if (expectedAnnexRevision === null) return { kind: 'refused', message: DAY_STORE_UNREADABLE_TEXT };
  const named = nameRefusal(input.name);
  if (named) return { kind: 'refused', message: named };
  const read = dayDetails(input.day);
  if (!read.ok) return { kind: 'refused', message: read.message };
  const { day, greeting, kind, collection } = read.details;
  return {
    kind: 'ready',
    body: { pngBase64: input.pngBase64, name: input.name.trim(), day, greeting, kind, ...(collection === null ? {} : { collection }), expectedRevision, expectedAnnexRevision },
  };
}

export type DayOutfitEdit =
  | { kind: 'unchanged' }
  | { kind: 'refused'; message: string }
  | { kind: 'ready'; name?: string; details?: DayDetails };

/**
 * What Edit's Save changes on a day outfit: a new name (PATCH, renameBody), new day details (PUT …/day, updateDayBody),
 * either or both, or nothing; or why the service would refuse it (the name first, then the day fields).
 */
export function dayOutfitEdit(
  outfit: Pick<AdminOutfit, 'name'>,
  day: Pick<AdminDay, 'rule' | 'greeting' | 'kind' | 'collection'>,
  input: { name: string; day: DayInputs },
): DayOutfitEdit {
  const change: { name?: string; details?: DayDetails } = {};
  const name = input.name.trim();
  if (name !== outfit.name) {
    const refusal = nameRefusal(input.name);
    if (refusal) return { kind: 'refused', message: refusal };
    change.name = name;
  }
  const read = dayDetails(input.day);
  if (!read.ok) return { kind: 'refused', message: read.message };
  const details = read.details;
  if (!sameDayRule(details.day, day.rule) || details.greeting !== day.greeting || details.kind !== day.kind || details.collection !== day.collection) {
    change.details = details;
  }
  return change.name === undefined && change.details === undefined ? { kind: 'unchanged' } : { kind: 'ready', ...change };
}

/** A day outfit's rename: PATCH with the name alone, never a window (its catalog window follows its rule). */
export function renameBody(name: string, expectedRevision: number): UpdateMascotOutfitBody {
  return { name, expectedRevision };
}

/** PUT …/day: the details, the collection always named (null takes it away), and both revisions. */
export function updateDayBody(details: DayDetails, expectedRevision: number, expectedAnnexRevision: number): UpdateDayBody {
  return { day: details.day, greeting: details.greeting, kind: details.kind, collection: details.collection, expectedRevision, expectedAnnexRevision };
}

/** POST …/restore: both revisions the card loaded. */
export function restoreBody(expectedRevision: number, expectedAnnexRevision: number): RestoreOutfitBody {
  return { expectedRevision, expectedAnnexRevision };
}

// ---- Dates ----

/** The date formats civilDateText has made, by locale and whether they name the weekday (each row writes three dates). */
const dateFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * A calendar date as the console writes it, "Thu, Nov 26, 2026" (or without the weekday), in the browser's locale: the
 * date itself, the same in every time zone.
 */
export function civilDateText(civil: number, locale?: string, weekday = true): string {
  const key = `${locale ?? ''}|${weekday}`;
  let format = dateFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...(weekday ? { weekday: 'short' as const } : {}), month: 'short', day: 'numeric', year: 'numeric' });
    if (dateFormats.size >= 16) dateFormats.clear();
    dateFormats.set(key, format);
  }
  return format.format(civil * DAY_MS);
}

/** A 'YYYY-MM-DD' date as civilDateText writes it (as it is, if it is not a date). */
export function isoDateText(iso: string, locale?: string): string {
  const civil = civilOfIso(iso);
  return civil === undefined ? iso : civilDateText(civil, locale);
}

/** One occurrence: "Thu, Nov 26, 2026", or a run of days, "Dec 5, 2026 to Dec 12, 2026 (8 days)". */
export function occurrenceText(found: DayOccurrence, locale?: string): string {
  if (found.span <= 1) return civilDateText(found.start, locale);
  return `${civilDateText(found.start, locale, false)} to ${civilDateText(found.start + found.span - 1, locale, false)} (${found.span} days)`;
}

/** A rule's next `count` occurrences going on today or to come, by the Pacific calendar (the service's nextDates). */
export function nextDays(rule: DayRule, now: number, count = 3): DayOccurrence[] {
  return nextOccurrences(rule, civilAt(DAY_CONSOLE_ZONE, now), count);
}

/**
 * The form's live preview and each row's dates: "Next (Pacific): Thu, Nov 26, 2026 · Thu, Nov 25, 2027 · Thu, Nov 23,
 * 2028" (today's counts while it is on), or why there are none.
 */
export function nextDatesText(rule: DayRule, now: number, locale?: string): string {
  const next = nextDays(rule, now, 3);
  if (next.length === 0) return rule.kind === 'once' ? 'Its day has gone; it does not come again.' : 'No date to come: enter the next year’s date.';
  return `Next (Pacific): ${next.map((found) => occurrenceText(found, locale)).join(' · ')}`;
}

/**
 * Where a day outfit is in its life at `now`, by the Pacific calendar: the engine's dayLifeState, which the service's
 * view gives as `state`, so the tabs can never sort an outfit differently from the service.
 */
export function dayStateAt(rule: DayRule, now: number): DayState {
  return dayLifeState(rule, civilAt(DAY_CONSOLE_ZONE, now));
}

/**
 * Whether a day outfit's day is going on somewhere on Earth at `now`: from its start in UTC+14 to its end in UTC-12
 * (the engine's showingSomewhere, the service's showingSomewhereNow). Someone, somewhere, may have it on while Pacific
 * time is not there yet.
 */
export function showingSomewhereAt(rule: DayRule, now: number): boolean {
  return showingSomewhere(rule, civilAt(DAY_CONSOLE_ZONE, now), now);
}

/** Whether the service swaps day outfits in at all: false while GYRO_DAY_OUTFITS=0 has them off (absent: on). */
export function dayOutfitsSwitchedOn(catalog: Partial<Pick<DesktopMascotCatalog, 'dayOutfitsOn'>>): boolean {
  return catalog.dayOutfitsOn !== false;
}

/** What the section says while the service has day outfits switched off. */
export const DAY_OUTFITS_OFF_TEXT = 'Day outfits are switched off on this service (GYRO_DAY_OUTFITS=0): nobody gets one or its greeting, and none is in '
  + 'anyone\'s gallery, until they are switched on again. Everything set up here is kept as it is.';

// ---- The tabs ----

export type DayTab = DayState | 'retired';

/** The tabs, in order, with what each says when it is empty. */
export const DAY_TABS: ReadonlyArray<{ tab: DayTab; label: string; empty: string }> = [
  { tab: 'today', label: 'Today', empty: 'No day outfit is on today (Pacific).' },
  { tab: 'scheduled', label: 'Scheduled', empty: 'No day outfit is scheduled.' },
  { tab: 'needs-date', label: 'Needs a date', empty: 'Every day outfit whose dates IT enters has one to come.' },
  { tab: 'past', label: 'Past', empty: 'No one-off day outfit has gone by.' },
  { tab: 'retired', label: 'Retired', empty: 'No outfit is retired.' },
];

/** A day outfit's row: the outfit, what the service said about it, and its state and dates at the card's clock. */
export interface DayRow {
  outfit: AdminOutfit;
  day: AdminDay;
  state: DayState;
  /** Its occurrence going on today or its next one, by the Pacific calendar; none when it has no date to come. */
  next: DayOccurrence | undefined;
  /** Swapped in today for people on Pacific time: the shortest day outfit on today's date, first in catalog order. */
  swappedIn: boolean;
  /** Its day is going on somewhere on Earth (showingSomewhereAt). */
  showingSomewhereNow: boolean;
}

/**
 * The ids of the day outfits whose rule this console cannot read (a newer gyro-svc's kind, while this console is older):
 * "Needs attention" lists them, never a tab, so nothing reads their rule's fields.
 */
export function unreadableDayIds(catalog: Partial<Pick<DesktopMascotCatalog, 'days'>>): Set<string> {
  const days = catalog.days ?? {};
  return new Set(Object.keys(days).filter((id) => parseDayRule(days[id]!.rule) === undefined));
}

/**
 * The day outfits by tab at `now`, by the Pacific calendar: Today (the one swapped in first, then catalog order),
 * Scheduled (by next date), Needs a date and Past (the latest first). A day outfit is a catalog outfit the service lists
 * in `days` with a rule this console reads (unreadableDayIds). While the service has day outfits switched off, none is
 * swapped in or on anywhere.
 */
export function dayRows(catalog: Pick<DesktopMascotCatalog, 'outfits'> & Partial<Pick<DesktopMascotCatalog, 'days' | 'dayOutfitsOn'>>, now: number): Record<DayState, DayRow[]> {
  const days = catalog.days ?? {};
  const unreadable = unreadableDayIds(catalog);
  const on = dayOutfitsSwitchedOn(catalog);
  const today = civilAt(DAY_CONSOLE_ZONE, now);
  const entries: DayEntry[] = [];
  catalog.outfits.forEach((outfit, order) => {
    if (hasOwn(days, outfit.id) && !unreadable.has(outfit.id)) entries.push({ id: outfit.id, rule: days[outfit.id]!.rule, order });
  });
  const swappedIn = on ? new DayIndex(entries).winner(today)?.entry.id : undefined;
  const rows: Record<DayState, DayRow[]> = { today: [], scheduled: [], 'needs-date': [], past: [] };
  for (const outfit of catalog.outfits) {
    if (!hasOwn(days, outfit.id) || unreadable.has(outfit.id)) continue;
    const day = days[outfit.id]!;
    const state = dayStateAt(day.rule, now);
    rows[state].push({
      outfit,
      day,
      state,
      next: nextOccurrences(day.rule, today, 1)[0],
      swappedIn: outfit.id === swappedIn,
      showingSomewhereNow: on && showingSomewhereAt(day.rule, now),
    });
  }
  rows.today.sort((a, b) => Number(b.swappedIn) - Number(a.swappedIn));
  rows.scheduled.sort((a, b) => (a.next?.start ?? Number.POSITIVE_INFINITY) - (b.next?.start ?? Number.POSITIVE_INFINITY));
  const lastDay = (row: DayRow) => (row.day.rule.kind === 'once' ? civilOfIso(row.day.rule.date) ?? 0 : 0);
  rows.past.sort((a, b) => lastDay(b) - lastDay(a));
  return rows;
}

/** The retired outfits, the most recently retired first. */
export function retiredRows(catalog: Partial<Pick<DesktopMascotCatalog, 'retired'>>): KeptOutfit[] {
  return [...(catalog.retired ?? [])].sort((a, b) => b.updatedAtMs - a.updatedAtMs);
}

/** How many outfits each tab holds. */
export function dayTabCounts(rows: Record<DayState, DayRow[]>, catalog: Partial<Pick<DesktopMascotCatalog, 'retired'>>): Record<DayTab, number> {
  return { today: rows.today.length, scheduled: rows.scheduled.length, 'needs-date': rows['needs-date'].length, past: rows.past.length, retired: (catalog.retired ?? []).length };
}

/** The tab the section opens on: Today while a day outfit is on, else Scheduled. */
export function firstDayTab(rows: Record<DayState, DayRow[]>): DayTab {
  return rows.today.length > 0 ? 'today' : 'scheduled';
}

/**
 * An outfit the admin should look at, with what fixes it: one that never reached the gallery (Restore finishes adding it,
 * Retire keeps it out), a retired one whose catalog copy a retire left behind (Retire again finishes it), or one whose
 * day rule a newer release wrote (hidden here; it can be retired).
 */
export interface AttentionRow {
  id: string;
  name: string;
  reason: 'orphan' | 'pending-tidy' | 'newer-rule';
  text: string;
  /** The gallery's thumbnail (base64, or its route for an outfit kept beyond the catalog: outfitPicture), or the image route for an outfit outside the gallery. */
  picture: { thumbnailPng: string } | { imageUrl: string };
  canRestore: boolean;
  retireLabel: string;
}

/** What "Needs attention" says about an outfit whose day rule a newer release wrote (it cannot be restored here). */
const NEWER_RULE_TEXT = 'Its day rule is from a newer release of Gyro, so it is hidden here and only that release can change or restore it. It can be retired.';

/**
 * The "Needs attention" list: the outfits that never reached the gallery, then the retires left unfinished, then the
 * rules from a newer release (in the catalog or not: the service lists one outside the catalog with the outfits that
 * never reached the gallery, and refuses to restore it, so it is offered Retire alone).
 */
export function attentionRows(catalog: Pick<DesktopMascotCatalog, 'outfits'> & Partial<Pick<DesktopMascotCatalog, 'orphans' | 'dayWarnings' | 'days'>>): AttentionRow[] {
  const newer = new Set((catalog.dayWarnings ?? []).flatMap((warning) => (warning.kind === 'newer-rule' ? [warning.outfitId] : [])));
  const rows = (catalog.orphans ?? []).map((outfit): AttentionRow => (newer.has(outfit.id)
    ? { id: outfit.id, name: outfit.name, reason: 'newer-rule', text: NEWER_RULE_TEXT, picture: { imageUrl: outfit.imageUrl }, canRestore: false, retireLabel: 'Retire' }
    : {
      id: outfit.id,
      name: outfit.name,
      reason: 'orphan',
      text: 'It never reached the gallery: an add that did not finish, or a day outfit an older Gyro deleted. Restore finishes adding it; Retire keeps it out.',
      picture: { imageUrl: outfit.imageUrl },
      canRestore: true,
      retireLabel: 'Retire',
    }));
  for (const warning of catalog.dayWarnings ?? []) {
    if (warning.kind !== 'pending-tidy' && warning.kind !== 'newer-rule') continue;
    const outfit = catalog.outfits.find((candidate) => candidate.id === warning.outfitId);
    if (!outfit) continue;
    rows.push(warning.kind === 'pending-tidy'
      ? {
        id: outfit.id,
        name: outfit.name,
        reason: 'pending-tidy',
        text: 'It is retired, but a retire that did not finish left its copy in the catalog (people do not see it). Finish retiring it, or restore it.',
        picture: outfitPicture(outfit),
        canRestore: true,
        retireLabel: 'Finish retiring',
      }
      : { id: outfit.id, name: outfit.name, reason: 'newer-rule', text: NEWER_RULE_TEXT, picture: outfitPicture(outfit), canRestore: false, retireLabel: 'Retire' });
  }
  // A day outfit whose rule this console cannot read (a newer gyro-svc's, while this console is older).
  for (const id of unreadableDayIds(catalog)) {
    const outfit = catalog.outfits.find((candidate) => candidate.id === id);
    if (!outfit || rows.some((row) => row.id === id)) continue;
    rows.push({
      id,
      name: outfit.name,
      reason: 'newer-rule',
      text: 'Its day rule is one this page does not know (the service is newer): reload the page to see and change it. It can be retired.',
      picture: outfitPicture(outfit),
      canRestore: false,
      retireLabel: 'Retire',
    });
  }
  const order = { orphan: 0, 'pending-tidy': 1, 'newer-rule': 2 } as const;
  return rows.sort((a, b) => order[a.reason] - order[b.reason]);
}

// ---- Warnings ----

/** An outfit's name by id, from the catalog, the retired outfits and those that never reached the gallery; "outfit <id>" for any other. */
export function outfitNameOf(catalog: Pick<DesktopMascotCatalog, 'outfits'> & Partial<Pick<DesktopMascotCatalog, 'retired' | 'orphans'>>): (id: string) => string {
  const names = new Map<string, string>();
  for (const outfit of [...(catalog.orphans ?? []), ...(catalog.retired ?? []), ...catalog.outfits]) names.set(outfit.id, outfit.name);
  return (id) => names.get(id) ?? `outfit ${id}`;
}

/** “A”, “A” and “B”, “A”, “B” and “C”. */
function namesText(names: ReadonlyArray<string>): string {
  const quotedNames = names.map(quoted);
  return quotedNames.length <= 1 ? quotedNames.join('') : `${quotedNames.slice(0, -1).join(', ')} and ${quotedNames[quotedNames.length - 1]}`;
}

/** A warning in the banner's words (dates written as civilDateText). */
export function dayWarningText(warning: DayWarning, nameOf: (id: string) => string, locale?: string): string {
  switch (warning.kind) {
    case 'clash': {
      const winner = quoted(nameOf(warning.winnerId));
      const others = warning.loserIds.map(nameOf);
      return `On ${isoDateText(warning.date, locale)}, ${namesText([nameOf(warning.winnerId), ...others])} share the day: ${winner} is swapped in, as it comes first in the gallery's order, `
        + `and ${others.length === 1 ? 'the other is' : 'the others are'} only in the gallery that day.`;
    }
    case 'span-note':
      return `${quoted(nameOf(warning.winnerId))} takes ${isoDateText(warning.date, locale)} from ${namesText(warning.loserIds.map(nameOf))}: the shorter day outfit wins its own date, as meant.`;
    case 'missing-next-year':
      return warning.severity === 'error'
        ? `${quoted(nameOf(warning.outfitId))} has no date to come, so nobody gets it: edit it and enter its next date.`
        : `${quoted(nameOf(warning.outfitId))} has no date for ${warning.year} yet: edit it and enter ${warning.year}’s date.`;
    case 'confirm-each-year':
      return warning.nextDate
        ? `Check ${quoted(nameOf(warning.outfitId))}’s date every year, as it is announced later: the next one entered is ${isoDateText(warning.nextDate, locale)}.`
        : `Check ${quoted(nameOf(warning.outfitId))}’s date every year, as it is announced later: none is entered for the year to come.`;
    case 'leap-only':
      return `${quoted(nameOf(warning.outfitId))} is on February 29, so it comes only in leap years${warning.nextDate ? `: next on ${isoDateText(warning.nextDate, locale)}` : ''}.`;
    case 'newer-rule':
      return `${quoted(nameOf(warning.outfitId))} has a day rule from a newer release of Gyro, so it is hidden here and only that release can change it.`;
    case 'pending-tidy':
      return `${quoted(nameOf(warning.outfitId))} is retired, but its copy is still in the catalog (a retire that did not finish); people do not see it. Finish retiring it under Needs attention.`;
  }
}

const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 } as const;

/**
 * The banner's warnings, the most serious first (errors, then warnings, then notes), each in date order where it has
 * one. Unfinished retires and rules from a newer release are left to the "Needs attention" list, with their buttons.
 */
export function bannerWarnings(warnings: ReadonlyArray<DayWarning>): DayWarning[] {
  const dateOf = (warning: DayWarning) => ('date' in warning ? warning.date : 'nextDate' in warning ? warning.nextDate ?? '' : '');
  return warnings
    .filter((warning) => warning.kind !== 'pending-tidy' && warning.kind !== 'newer-rule')
    .map((warning, index) => ({ warning, index }))
    .sort((a, b) => SEVERITY_ORDER[a.warning.severity] - SEVERITY_ORDER[b.warning.severity]
      || (dateOf(a.warning) < dateOf(b.warning) ? -1 : dateOf(a.warning) > dateOf(b.warning) ? 1 : 0)
      || a.index - b.index)
    .map(({ warning }) => warning);
}

// ---- Stars ----

/** "★ 4.3 (12)", "No ratings yet", or null while the service cannot read the ratings (or is from before them). */
export function ratingText(rating: OutfitRating | undefined): string | null {
  if (!rating) return null;
  if (rating.count <= 0 || rating.average === null) return 'No ratings yet';
  return `★ ${rating.average.toFixed(1)} (${rating.count.toLocaleString('en-US')})`;
}

/** The same for a screen reader: "Rated 4.3 out of 5 by 12 people", "Nobody has rated it yet", or null. */
export function ratingLabel(rating: OutfitRating | undefined): string | null {
  if (!rating) return null;
  if (rating.count <= 0 || rating.average === null) return 'Nobody has rated it yet';
  return `Rated ${rating.average.toFixed(1)} out of 5 by ${rating.count === 1 ? '1 person' : `${rating.count.toLocaleString('en-US')} people`}`;
}

/** How many gave each of 1 to 5 stars (0 for any count the service left out). */
function starCounts(rating: OutfitRating): number[] {
  return [0, 1, 2, 3, 4].map((index) => {
    const value = rating.distribution[index];
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
  });
}

/** Each of 1 to 5 stars' share of the ratings, from 0 to 1 (all 0 when nobody rated it): the distribution bar. */
export function ratingShares(rating: OutfitRating): number[] {
  const counts = starCounts(rating);
  const total = counts.reduce((sum, value) => sum + value, 0);
  return counts.map((value) => (total > 0 ? value / total : 0));
}

/** The distribution in words, 5 stars first: "5 stars: 6 · 4 stars: 3 · 3 stars: 2 · 2 stars: 0 · 1 star: 1". */
export function distributionText(rating: OutfitRating): string {
  const counts = starCounts(rating);
  return [5, 4, 3, 2, 1].map((stars) => `${stars} star${stars === 1 ? '' : 's'}: ${counts[stars - 1]!.toLocaleString('en-US')}`).join(' · ');
}

// ---- Notes ----

/**
 * The note after a day outfit is added: when it comes next by the Pacific calendar, and where it is listed. On today,
 * whether people get it: only the day outfit swapped in today (the shortest on the date, then the first in catalog order,
 * where a new one is last) replaces their Gyro; one that shares today with another is only in the gallery today. Nobody
 * gets any while the service has day outfits switched off.
 */
export function dayAddedText(added: DesktopMascotAdded, now: number, locale?: string): string {
  const outfit = added.outfits.find((item) => item.id === added.outfitId);
  const day = added.days && hasOwn(added.days, added.outfitId) ? added.days[added.outfitId] : undefined;
  const name = outfit ? quoted(outfit.name) : 'the day outfit';
  const stripped = added.strippedChunks.length > 0 ? ` Removed from the file before storing: ${added.strippedChunks.join(', ')}.` : '';
  if (!day) return `Added ${name}. The change is in the audit trail.${stripped}`;
  const next = nextDays(day.rule, now, 1)[0];
  let when: string;
  if (!dayOutfitsSwitchedOn(added)) {
    when = ' Day outfits are switched off on this service, so nobody gets it until they are switched on again.';
  } else if (!next) {
    when = ' It has no date to come yet.';
  } else if (dayStateAt(day.rule, now) === 'today') {
    const winner = dayRows(added, now).today.find((row) => row.swappedIn);
    when = !winner || winner.outfit.id === added.outfitId
      ? ' It is on today (Pacific): people get it on their next check.'
      : ` It is on today (Pacific), but ${quoted(winner.outfit.name)} is swapped in today, so this one is only in people's gallery today.`;
  } else {
    when = ` It comes next on ${occurrenceText(next, locale)} (Pacific).`;
  }
  return `Added the day outfit ${name}.${when} It is listed under Day outfits. The change is in the audit trail.${stripped}`;
}

/** The note after Edit's Save on a day outfit. */
export function daySavedText(name: string): string {
  return `Saved ${quoted(name)}. People see the change on their next check, within about 20 minutes. The change is in the audit trail.`;
}

/**
 * The note after Restore: back as a day outfit (with its next date) or in the gallery with the window it had, in the
 * place it had in the gallery's order. A restored outfit is never featured (`wasFeatured`: it was when it was retired),
 * and one whose window has ended is in nobody's gallery until its window changes.
 */
export function restoredText(view: Pick<DesktopMascotCatalog, 'outfits' | 'days'>, id: string, now: number, locale?: string, wasFeatured = false): string {
  const outfit = view.outfits.find((item) => item.id === id);
  const name = outfit ? quoted(outfit.name) : 'the outfit';
  const day = view.days && hasOwn(view.days, id) ? view.days[id] : undefined;
  if (day) {
    const next = nextDays(day.rule, now, 1)[0];
    return `Restored ${name} as a day outfit${next ? `; it comes next on ${occurrenceText(next, locale)} (Pacific)` : ''}. The change is in the audit trail.`;
  }
  const featured = wasFeatured ? ' It was featured; it comes back unfeatured, so feature it again if people who have not picked an outfit should get it.' : '';
  const ended = outfit && outfitState(outfit.window, now) === 'ended' ? ' Its window has ended, so nobody sees it until you change its window.' : '';
  return `Restored ${name} to the gallery with the window it had, in its own place in the gallery's order.${ended}${featured} The change is in the audit trail.`;
}
